// src/store/slices/authSlice.ts
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { apiClient } from '@/api/client'
import type { User, LoginCredentials, RegisterData, AuthTokens } from '@/types'

interface AuthState {
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean

  // Actions
  login: (credentials: LoginCredentials) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => void
  setTokens: (accessToken: string, refreshToken: string) => void
  setUser: (user: User) => void
  updateUser: (user: Partial<User>) => Promise<void>
  refreshUser: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,

      login: async (credentials) => {
        set({ isLoading: true })
        try {
          const tokens = await apiClient.post<AuthTokens>(
            '/v1/auth/login',
            credentials
          )
          const user = await apiClient.get<User>('/v1/auth/me', {
            headers: { Authorization: `Bearer ${tokens.access_token}` },
          })

          set({
            user,
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            isAuthenticated: true,
            isLoading: false,
          })

          // Сохраняем язык пользователя
          if (user.language) {
            localStorage.setItem('language', user.language)
          }

          return Promise.resolve()
        } catch (error) {
          set({ isLoading: false })
          return Promise.reject(error)
        }
      },

      register: async (data) => {
        set({ isLoading: true })
        try {
          try {
            await apiClient.post<User>('/v1/auth/register', data)
          } catch (error) {
            const response = (error as { response?: { status?: number; data?: { detail?: string } } })?.response
            const alreadyRegistered = response?.status === 409
              && response.data?.detail?.toLowerCase().includes('already exists')
            if (!alreadyRegistered) throw error
          }
          const tokens = await apiClient.post<AuthTokens>(
            '/v1/auth/login',
            { email: data.email, password: data.password }
          )
          const user = await apiClient.get<User>('/v1/auth/me', {
            headers: { Authorization: `Bearer ${tokens.access_token}` },
          })

          set({
            user,
            accessToken: tokens.access_token,
            refreshToken: tokens.refresh_token,
            isAuthenticated: true,
            isLoading: false,
          })

          if (user.language) {
            localStorage.setItem('language', user.language)
          }

          return Promise.resolve()
        } catch (error) {
          set({ isLoading: false })
          return Promise.reject(error)
        }
      },

      logout: () => {
        // Отправляем запрос на logout
        apiClient.post('/v1/auth/logout').catch(() => {})

        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
        })

        localStorage.removeItem('language')
        window.location.href = '/login'
      },

      setTokens: (accessToken, refreshToken) => {
        set({ accessToken, refreshToken, isAuthenticated: true })
      },

      setUser: (user) => {
        set({ user })
        if (user.language) {
          localStorage.setItem('language', user.language)
        }
      },

      updateUser: async (userData) => {
        set({ isLoading: true })
        try {
          const response = await apiClient.patch<User>('/v1/users/me', userData)
          set({ user: response, isLoading: false })
          return Promise.resolve()
        } catch (error) {
          set({ isLoading: false })
          return Promise.reject(error)
        }
      },

      refreshUser: async () => {
        try {
          const user = await apiClient.get<User>('/v1/users/me')
          set({ user })
          if (user.language) {
            localStorage.setItem('language', user.language)
          }
        } catch (error) {
          // Если не удалось получить пользователя, выходим
          if (error instanceof Error && 'response' in error && (error as any).response?.status === 401) {
            get().logout()
          }
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
